import { DeleteOutlined, EyeOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Drawer, Form, Input, InputNumber, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePagination } from "@/shared/hooks/usePagination";
import { nvrService } from "../services/nvr.service";
import { weddingHallService } from "@/modules/wedding-halls/services/weddingHall.service";

const { Title } = Typography;

export function NvrsPage() {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const queryClient = useQueryClient();
	const { page, pageSize, onChange } = usePagination();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [form] = Form.useForm();

	const { data, isLoading } = useQuery({
		queryKey: ["nvrs", { page, per_page: pageSize }],
		queryFn: () => nvrService.list({ page, per_page: pageSize }),
	});

	const { data: hallsData } = useQuery({
		queryKey: ["wedding-halls-select"],
		queryFn: () => weddingHallService.list({ per_page: 100 }),
	});

	const saveMutation = useMutation({
		mutationFn: (v: Record<string, unknown>) => editingId ? nvrService.update(editingId, v) : nvrService.create(v),
		onSuccess: () => {
			message.success(editingId ? "Yangilandi" : "Qo'shildi");
			closeDrawer();
			queryClient.invalidateQueries({ queryKey: ["nvrs"] });
		},
		onError: () => message.error("Xatolik"),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => nvrService.remove(id),
		onSuccess: () => { message.success("O'chirildi"); queryClient.invalidateQueries({ queryKey: ["nvrs"] }); },
	});

	const syncMutation = useMutation({
		mutationFn: (id: number) => nvrService.sync(id),
		onSuccess: () => { message.success("Sinxronlandi"); queryClient.invalidateQueries({ queryKey: ["nvrs"] }); },
	});

	const openCreate = () => { setEditingId(null); form.resetFields(); setDrawerOpen(true); };
	const openEdit = (record: Record<string, unknown>) => { setEditingId(record.id as number); form.setFieldsValue(record); setDrawerOpen(true); };
	const closeDrawer = () => { setDrawerOpen(false); setEditingId(null); form.resetFields(); };

	const nvrs = data?.data?.nvrs || [];
	const pagination = data?.data?.pagination;
	const stateColor: Record<string, string> = { online: "green", offline: "red", unknown: "default" };

	const columns = [
		{ title: "ID", dataIndex: "id", width: 60 },
		{
			title: "Nomi", dataIndex: "name",
			render: (v: string, r: Record<string, unknown>) => (
				<a onClick={() => navigate(`/nvrs/${r.id}`)}>{v}</a>
			),
		},
		{ title: "To'yxona", key: "hall", render: (_: unknown, r: Record<string, unknown>) => (r.wedding_hall as { name: string } | null)?.name || "—" },
		{ title: "IP", dataIndex: "ip" },
		{ title: "Serial", dataIndex: "serial" },
		{ title: "Holat", dataIndex: "state", render: (v: string) => <Tag color={stateColor[v]}>{v}</Tag> },
		{ title: "Sync", dataIndex: "is_sync", render: (v: number) => <Tag color={v ? "green" : "orange"}>{v ? "Ha" : "Yo'q"}</Tag> },
		{ title: "Kameralar", dataIndex: "camera_count" },
		{
			title: "Amallar", width: 140,
			render: (_: unknown, r: Record<string, unknown>) => (
				<Space>
					<Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/nvrs/${r.id}`)} title="Batafsil" />
					<Button size="small" icon={<SyncOutlined />} onClick={() => syncMutation.mutate(r.id as number)} loading={syncMutation.isPending} title="Sinxronlash" />
					<Button size="small" onClick={() => openEdit(r)} title="Tahrirlash">Edit</Button>
					<Popconfirm title="O'chirish?" onConfirm={() => deleteMutation.mutate(r.id as number)}>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">NVR qurilmalar</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Qo'shish</Button>
			</div>
			<Table columns={columns} dataSource={nvrs} rowKey="id" loading={isLoading}
				pagination={{ current: pagination?.current_page, pageSize: pagination?.per_page, total: pagination?.total_items, onChange, showSizeChanger: true }} />

			<Drawer title={editingId ? "NVR tahrirlash" : "Yangi NVR"} open={drawerOpen} onClose={closeDrawer} width={480}
				extra={<Space><Button onClick={closeDrawer}>Bekor qilish</Button><Button type="primary" onClick={() => form.submit()} loading={saveMutation.isPending}>Saqlash</Button></Space>}>
				<Form form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
					<Form.Item name="wedding_hall_id" label="To'yxona" rules={[{ required: true }]}>
						<Select placeholder="Tanlang" showSearch optionFilterProp="label"
							options={hallsData?.data?.wedding_halls?.map((h: { id: number; name: string }) => ({ value: h.id, label: h.name }))} />
					</Form.Item>
					<Form.Item name="name" label="Nomi" rules={[{ required: true }]}><Input /></Form.Item>
					<Form.Item name="ip" label="IP manzil"><Input placeholder="192.168.1.1" /></Form.Item>
					<Form.Item name="serial" label="Serial raqam"><Input /></Form.Item>
					<Form.Item name="device_id" label="Device ID" rules={[{ required: true }]}><Input /></Form.Item>
					<Form.Item name="secret_key" label="Secret Key" rules={[{ required: true }]}><Input /></Form.Item>
					<Form.Item name="brand" label="Brand"><Input placeholder="hikvision" /></Form.Item>
					<Form.Item name="model" label="Model"><Input /></Form.Item>
					<Form.Item name="channel_count" label="Kanal soni"><InputNumber className="w-full" /></Form.Item>
				</Form>
			</Drawer>
		</div>
	);
}
